import React, { useEffect, useState, useMemo } from "react";
import { getBMPCostDeduction } from "../../api/getBMPCostDeduction";
import { BMP_CostDeduction, ContactPersonType } from "../../models/bmp.model";
import { ArrowLeftIcon } from "./icons/ArrowLeftIcon";
import { EmptyIcon } from "./icons/EmptyIcon";
import { ChevronDownIcon } from "../../styles/assets/svgs/ChevronDownIcon";
import { responseHero } from "../../utils/axiosInterceptor";

// --- 1. MODAL KOMPONENS (Külön fájlba szántad, de ide raktam az átláthatóságért) ---

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: string) => void;
  itemName: string;
  amount: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onConfirm, itemName, amount }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-dark-blue/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-cream animate-in zoom-in duration-200">
        <div className="p-6">
          <h3 className="text-xl font-bold text-dark-blue mb-2">Kifizetés rögzítése</h3>
          <p className="text-sm text-light-blue mb-4">
            Megerősíted a kifizetést? <br />
            <span className="font-semibold text-dark-brown">({itemName} - {amount})</span>
          </p>
          <div className="space-y-2">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-dark-brown">Dátum</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full p-3 border border-cream rounded-xl focus:ring-2 focus:ring-brown outline-none text-dark-blue transition-all"
            />
          </div>
        </div>
        <div className="bg-ultra-light p-4 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-light-blue hover:text-dark-blue transition-colors">Mégse</button>
          <button 
            onClick={() => onConfirm(selectedDate)} 
            className="px-6 py-2 bg-brown hover:bg-dark-brown text-white text-sm font-bold rounded-xl shadow-md transition-transform active:scale-95"
          >
            Kifizetés rögzítése
          </button>
        </div>
      </div>
    </div>
  );
};

// --- 2. FŐ TÁBLÁZAT KOMPONENS ---

interface CostDeductionTableProps {
    onBack?: () => void;
}

export const CostDeductionTable: React.FC<CostDeductionTableProps> = ({ onBack }) => {
    const [entities, setEntities] = useState<BMP_CostDeduction[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const result = await getBMPCostDeduction();
            setEntities(result);
        } catch (error) {
            console.error("Error fetching cost deduction data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatMoney = (amount: number) => {
        return new Intl.NumberFormat("hu-HU", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const groupedData = useMemo(() => {
        const groups: Record<string, any> = {};
        entities.forEach((item) => {
            const date = new Date(item.date);
            const monthLabel = `${date.getFullYear()}. ${date.toLocaleString('hu-HU', { month: 'long' })}`;
            const amount = Number(item.amount || 0);

            if (!groups[monthLabel]) {
                groups[monthLabel] = { label: monthLabel, humanDC: 0, csaba: 0, david: 0, total: 0, items: [] };
            }

            if (item.contactType === ContactPersonType.HDC) groups[monthLabel].humanDC += amount;
            else if (item.contactType === ContactPersonType.CSABA) groups[monthLabel].csaba += amount;
            else if (item.contactType === ContactPersonType.DAVID) groups[monthLabel].david += amount;

            groups[monthLabel].total += amount;
            groups[monthLabel].items.push(item);
        });
        return Object.values(groups);
    }, [entities]);

    const grandTotal = useMemo(() => groupedData.reduce((acc, curr) => acc + curr.total, 0), [groupedData]);

    return (
        <div className="min-h-screen bg-back p-6 sm:p-10 font-sans text-dark-blue">
            <div className="max-w-[1200px] mx-auto mb-8">
                <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-brown hover:text-dark-brown transition-colors mb-2 uppercase tracking-wide">
                    <ArrowLeftIcon /> Vissza
                </button>
                <h1 className="text-3xl font-extrabold text-dark-blue tracking-tight">Költséglevonások</h1>
                <p className="text-light-blue mt-1">A levont jutalékok havi és partneri bontása.</p>
            </div>

            <div className="max-w-[1200px] mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-cream">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-cream border-b border-light-brown/20 text-dark-brown">
                                <th className="p-5 font-bold text-[11px] uppercase tracking-wider">Hónap / Részletek</th>
                                <th className="p-5 font-bold text-[11px] uppercase tracking-wider text-right">Human DC</th>
                                <th className="p-5 font-bold text-[11px] uppercase tracking-wider text-right">Csaba</th>
                                <th className="p-5 font-bold text-[11px] uppercase tracking-wider text-right">David</th>
                                <th className="p-5 font-bold text-[11px] uppercase tracking-wider text-right bg-dark-blue/5">Összesen</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ultra-light">
                            {isLoading ? (
                                <tr><td colSpan={5} className="p-12 text-center"><div className="animate-spin inline-block w-8 h-8 border-4 border-brown border-t-transparent rounded-full" /></td></tr>
                            ) : (
                                groupedData.map((group) => (
                                    <CollapsibleRow key={group.label} group={group} formatMoney={formatMoney} onRefresh={fetchData} />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// --- 3. ALREKORD SOR ÉS KIFIZETÉS LOGIKA ---

const CollapsibleRow = ({ group, formatMoney, onRefresh }: { group: any, formatMoney: any, onRefresh: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<BMP_CostDeduction | null>(null);

    const confirmPayment = async (payoutDate: string) => {
        if (!selectedItem) return;

        try {
            // BEÉPÍTETT API HÍVÁS
            
            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/cost/pay`, {
                id: selectedItem.id, 
                date: payoutDate 
            });

            setSelectedItem(null);
            onRefresh(); // Adatok újratöltése a táblázatban
        } catch (error) {
            console.error(error);
            alert("Hiba történt a mentéskor!");
        }
    };

    return (
        <>
            <tr onClick={() => setIsOpen(!isOpen)} className="cursor-pointer hover:bg-ultra-light/50 transition-colors group">
                <td className="p-5 text-dark-blue font-bold text-sm flex items-center gap-2">
                    <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}><ChevronDownIcon /></div>
                    {group.label}
                </td>
                <td className="p-5 text-right font-mono text-sm">{formatMoney(group.humanDC)}</td>
                <td className="p-5 text-right font-mono text-sm">{formatMoney(group.csaba)}</td>
                <td className="p-5 text-right font-mono text-sm">{formatMoney(group.david)}</td>
                <td className="p-5 text-right font-bold font-mono text-sm bg-ultra-light/10 text-dark-brown">{formatMoney(group.total)}</td>
            </tr>
            {isOpen && (
                <tr className="bg-back/30">
                    <td colSpan={5} className="p-0">
                        <div className="px-10 py-4 border-l-4 border-brown animate-in fade-in slide-in-from-top-1 duration-200">
                            <table className="w-full text-xs text-light-blue">
                                <thead>
                                    <tr className="border-b border-cream text-[10px] uppercase">
                                        <th className="py-2 text-left">Dátum</th>
                                        <th className="py-2 text-left">Befektetés ID</th>
                                        <th className="py-2 text-left">Típus</th>
                                        <th className="py-2 text-right">Összeg</th>
                                        <th className="py-2 text-right">Státusz</th>
                                        <th className="py-2 text-right">Művelet</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map((item: BMP_CostDeduction) => (
                                        <tr key={item.id} className="border-b border-cream/50 last:border-0 hover:bg-white/50">
                                            <td className="py-2">{new Date(item.date).toLocaleDateString('hu-HU')}</td>
                                            <td className="py-2">#{item.investmentId}</td>
                                            <td className="py-2 font-medium">{item.contactType}</td>
                                            <td className="py-2 text-right font-mono">{formatMoney(Number(item.amount))}</td>
                                            <td className="py-2 text-right">
                                                {item.isPaid ? 
                                                    <span className="text-green-600 font-bold">✓ Kifizetve</span> : 
                                                    <span className="text-amber-600">Függőben</span>
                                                }
                                            </td>
                                            <td className="py-2 text-right">
                                                {!item.isPaid && (
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); setSelectedItem(item); }}
                                                        className="px-3 py-1 bg-brown/10 text-brown hover:bg-brown hover:text-white rounded-lg text-[10px] font-bold transition-all uppercase"
                                                    >
                                                        Kifizetés
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            )}

            {/* Modál meghívása */}
            <PaymentModal 
                isOpen={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                onConfirm={confirmPayment}
                itemName={`#${selectedItem?.investmentId}`}
                amount={formatMoney(Number(selectedItem?.amount || 0))}
            />
        </>
    );
};